import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Zap, Mail, MessageSquare, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Channel = ({ channel, icon: Icon, title, master, onMaster, items, onToggle, saving }) => (
    <div className={`rounded-xl border ${master ? 'border-[#A7F3D0] bg-white' : 'border-[#FDE68A] bg-[#FFFBEB]/40'}`} data-testid={`automations-${channel}`}>
        <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="flex items-center gap-2"><Icon className="h-5 w-5 text-[#064E3B]" /><div><p className="font-semibold text-[#1F2937]">{title}</p><p className="text-xs text-[#6B7280]">{master ? 'Master switch on — individual switches apply' : 'Master switch OFF — nothing in this list is sent'}</p></div></div>
            <label className="flex items-center gap-2 text-sm font-medium"><span className={master ? 'text-[#047857]' : 'text-[#B45309]'}>{master ? 'All on' : 'All off'}</span><Switch checked={master} disabled={saving} onCheckedChange={onMaster} data-testid={`automations-master-${channel}`} /></label>
        </div>
        <ul className="divide-y">
            {items.map(it => (
                <li key={it.key} className={`flex items-center justify-between gap-4 px-4 py-3 ${master ? '' : 'opacity-60'}`} data-testid={`automation-row-${it.key}`}>
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-[#1F2937] flex items-center gap-2">{it.label}<Badge variant="outline" className="text-[10px] font-normal">{it.group}</Badge></p>
                        <p className="text-xs text-[#6B7280]">{it.description}</p>
                    </div>
                    <Switch checked={it.enabled} disabled={saving} onCheckedChange={(v) => onToggle(it.key, v)} data-testid={`automation-toggle-${it.key}`} />
                </li>
            ))}
        </ul>
    </div>
);

export const AutomationsPanel = () => {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const load = useCallback(async () => {
        try { const r = await axios.get(`${API_URL}/api/automations`, { withCredentials: true }); setData(r.data); } catch { /* not super admin */ }
    }, []);
    useEffect(() => { load(); }, [load]);
    const save = async (payload, msg) => {
        setSaving(true);
        try { const r = await axios.put(`${API_URL}/api/automations`, payload, { withCredentials: true }); setData(r.data); toast.success(msg); }
        catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
        finally { setSaving(false); }
    };
    if (!data) return null;
    const emails = data.items.filter(i => i.channel === 'email');
    const sms = data.items.filter(i => i.channel === 'sms');
    const onToggle = (key, v) => { const it = data.items.find(i => i.key === key); save({ toggles: { [key]: v } }, `${it?.label} ${v ? 'enabled' : 'disabled'}`); };
    return (
        <Card className="card-surface" data-testid="automations-card">
            <CardHeader>
                <CardTitle className="font-['Outfit'] flex items-center gap-2"><Zap className="h-5 w-5 text-[#064E3B]" />Automatic emails & SMS {saving && <Loader2 className="h-4 w-4 animate-spin text-[#6B7280]" />}</CardTitle>
                <p className="text-sm text-[#4B5563]">Every message the system sends on its own. Turn any of them off here — messages you compose yourself (Communications Center, SMS broadcast, reminders you trigger) are never affected.</p>
            </CardHeader>
            <CardContent className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <Channel channel="email" icon={Mail} title="Automatic emails" master={data.master_email} onMaster={(v) => save({ master_email: v }, `Automatic emails ${v ? 'enabled' : 'disabled'}`)} items={emails} onToggle={onToggle} saving={saving} />
                <Channel channel="sms" icon={MessageSquare} title="Automatic SMS" master={data.master_sms} onMaster={(v) => save({ master_sms: v }, `Automatic SMS ${v ? 'enabled' : 'disabled'}`)} items={sms} onToggle={onToggle} saving={saving} />
            </CardContent>
        </Card>
    );
};
